import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import { IPageV2 } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/IPageV2";
import PagedResult from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/PagedResult";
import GenericCrudServiceV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/GenericCrudServiceV2";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import ChatUserDataDTO from "@/models/chat/ChatUserDataDTO";
import { LazoUser } from "@/models/LazoUser";
import { LazoUserWithInvitationDTO } from "@/models/LazoUserWithInvitationDTO";
import { SmallUserDTO } from "@/models/SmallUserDTO";
import { LazoApplicationOptions } from "@/types/LazoApplicationOptions";
import { LazoUserFilters } from "@/types/LazoUserFilters";
import { TinyUser } from "@/types/TinyUser";
import UserCreation from "@/types/UserCreation";

class LazoUserService extends GenericCrudServiceV2<LazoUser> {
  path: string;
  constructor(path: string) {
    super(path);
    this.path = path;
  }

  async getAllUsers(): Promise<ApiResponseV2<LazoUser[]>> {
    return HttpClientV2.get(`${this.path}/all-unpaged`);
  }

  searchUsersForPortalChat(term?: string): Promise<ApiResponseV2<ChatUserDataDTO[]>> {
    let qry = "";
    if (term) {
      qry += `?term=${encodeURIComponent(term)}`;
    }
    return HttpClientV2.get(`${this.path}/search/chat-user-data${qry}`);
  }

  getByCoreUser(): Promise<ApiResponseV2<LazoUser>> {
    return HttpClientV2.get(`${this.path}/get-by-core-user`, {
      axiosConfig: { timeout: 30000 },
    });
  }

  saveOptions(lazoUserExternalId: string, options: LazoApplicationOptions): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/${lazoUserExternalId}/save-options`, options);
  }

  searchByTerm(term: string): Promise<ApiResponseV2<LazoUser[]>> {
    return HttpClientV2.get(`${this.path}/query?term=${term}`);
  }

  searchUserByTenantAndTerm(term: string, externalId: string): Promise<ApiResponseV2<LazoUser[]>> {
    return HttpClientV2.get(`${this.path}/query-tenant?term=${term}&externalId=${externalId}`);
  }

  markUserInMultiUserChat(externalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/${externalId}/mark-in-multiuser-chat`, {});
  }

  getByCoreUserExternalId(coreUserExternalId: string): Promise<ApiResponseV2<LazoUser>> {
    const params = new URLSearchParams({
      coreUserExternalId,
    });

    return HttpClientV2.get(`${this.path}/by-core-user-external-id`, { axiosConfig: { params } });
  }

  getAllByTenantExternalId(tenantExternalId: string): Promise<ApiResponseV2<LazoUser[]>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}`);
  }

  getByEmail(userEmail: string): Promise<ApiResponseV2<LazoUser>> {
    return HttpClientV2.get(`${this.path}/email?userEmail=${userEmail}`);
  }

  getLazoEmployees(): Promise<ApiResponseV2<LazoUser[]>> {
    return HttpClientV2.get(`${this.path}/lazo-employees`);
  }

  getLazoClientsByTenant(externalIds: string[]): Promise<ApiResponseV2<LazoUser[]>> {
    return HttpClientV2.get(`${this.path}/lazo-clients-by-tenant?tenantExternalIds=${externalIds.join(",")}`);
  }

  getAllKam(): Promise<ApiResponseV2<LazoUser[]>> {
    return HttpClientV2.get(`${this.path}/all-kam`);
  }

  async checkIfUserHasAnsweredActivityForm(userExternalId: string): Promise<ApiResponseV2<boolean>> {
    return HttpClientV2.get(`${this.path}/has-answered-activity-form/${userExternalId}`);
  }

  getAllByTenantExternalIdPaged(
    tenantExternalId: string,
    page: number,
    pageSize: number
  ): Promise<ApiResponseV2<IPageV2<LazoUserWithInvitationDTO>>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}/paged?page=${page}&pageSize=${pageSize}`);
  }

  searchUserByTermAndTenant(
    tenantExternalId: string,
    term: string,
    page: number,
    pageSize: number
  ): Promise<ApiResponseV2<PagedResult<LazoUser>>> {
    return HttpClientV2.get(`${this.path}/query/paged?tenantExternalId=${tenantExternalId}&term=${term}&page=${page}&pageSize=${pageSize}`);
  }

  filter(filters: LazoUserFilters, tenantExternalId: string, page: number, pageSize: number): Promise<ApiResponseV2<IPageV2<LazoUser>>> {
    return HttpClientV2.post(`${this.path}/filter?tenantExternalId=${tenantExternalId}&page=${page}&pageSize=${pageSize}`, filters);
  }

  getKamUserByTenantExternalId(tenantExternalId: string): Promise<ApiResponseV2<LazoUser>> {
    return HttpClientV2.get(`${this.path}/get-kam-user-by-tenant?externalId=${tenantExternalId}`);
  }

  getSupportUser(): Promise<ApiResponseV2<LazoUser>> {
    return HttpClientV2.get(`${this.path}/support-user`);
  }

  getAssignedUsersByTerm(term: string): Promise<ApiResponseV2<SmallUserDTO[]>> {
    return HttpClientV2.get(`${this.path}/task/assigned-users?term=${term}`);
  }

  getChatUserSuggestions(term: string, tenantExternalId: string, conversationExternalId: string): Promise<ApiResponseV2<SmallUserDTO[]>> {
    let qry = `?term=${term}&tenantExternalId=${tenantExternalId}`;
    if (conversationExternalId) {
      qry += `&conversationExternalId=${conversationExternalId}`;
    }
    return HttpClientV2.get(`${this.path}/chat/user-suggestions${qry}`);
  }

  /**
   * @description This is meant to be used by a Lazo portal admin to create a user without an invitation.
   * */
  createUser(dto: UserCreation): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/admin/create-user`, dto);
  }

  getTenantsUsers(): Promise<ApiResponseV2<LazoUser[]>> {
    return HttpClientV2.get(`${this.path}/tenants/users`);
  }

  getServiceTeamUsers(): Promise<ApiResponseV2<TinyUser[]>> {
    return HttpClientV2.get(`${this.path}/service-team`);
  }

  searchUserSuggestionsByTermUnpaged(term: string): Promise<ApiResponseV2<PagedResult<LazoUser>>> {
    return this.searchUserSuggestionsByTerm(term, 0, -1);
  }

  searchUserSuggestionsByTerm(term: string, page?: number, pageSize?: number): Promise<ApiResponseV2<PagedResult<LazoUser>>> {
    let qryStr = `?term=${encodeURIComponent(term)}`;
    if (page) {
      qryStr += `&page=${page}`;
    }
    if (pageSize) {
      qryStr += `&pageSize=${pageSize}`;
    }
    return HttpClientV2.get(`${this.path}/suggestions${qryStr}`);
  }

  searchUserSuggestionsByTermAndTenant(
    term: string,
    tenantExternalId: string,
    page?: number,
    pageSize?: number
  ): Promise<ApiResponseV2<PagedResult<LazoUser>>> {
    let qryStr = `?term=${term}&tenantExternalId=${tenantExternalId}`;
    if (page) {
      qryStr += `&page=${page}`;
    }
    if (pageSize) {
      qryStr += `&pageSize=${pageSize}`;
    }
    return HttpClientV2.get(`${this.path}/suggestions-by-tenant${qryStr}`);
  }

  getLanguage(userExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.get(`${this.path}/${userExternalId}/language`);
  }

  updateLanguage(userExternalId: string, locale: string): Promise<ApiResponseV2<string>> {
    const params = new URLSearchParams({ locale });
    return HttpClientV2.patchWithoutPayload(`${this.path}/${userExternalId}/language`, { axiosConfig: { params } });
  }
}

export default new LazoUserService("/api/lazo-user");
